import { useEffect, useState } from 'react'
import { getCountryCode } from 'src/business/services'
import {
  EVENT_TEMPLATE_DATA,
  EVENT_SUBTYPE_INDIVIDUAL_THERAPY,
  SESSION_PLATFORM_HOME_VISIT,
  SESSION_PLATFORM_OFFLINE,
  SESSION_PLATFORM_ONLINE,
  allAvailableEventTypes,
} from 'src/consumer/constants'
import { getMPDataWithoutParams } from 'src/mentor/services'
import { getDurationOptions } from 'src/mentor/components/MentorEventsPage/MentorEventsPageStyles'

const VALID_MEETING_SRC_VALUES = new Set([
  SESSION_PLATFORM_ONLINE,
  SESSION_PLATFORM_OFFLINE,
  SESSION_PLATFORM_HOME_VISIT,
])

function normalizeInboundEventFormData(data) {
  if (!data) return null
  const clone = { ...data }

  if (!VALID_MEETING_SRC_VALUES.has(clone.meetingSrc)) {
    clone.meetingSrc = SESSION_PLATFORM_ONLINE
  }

  const rawAssessments = clone.psychAssessmentIds ?? clone.clinicalAssessmentIds
  if (Array.isArray(rawAssessments) && rawAssessments.length) {
    clone.psychAssessmentIds = rawAssessments.map((item) => {
      if (item && typeof item === 'object') {
        const value = item.value ?? item.id
        const label = item.label ?? item.title ?? item.name ?? value
        if (value) {
          return { value, label }
        }
      }
      if (typeof item === 'string') {
        return { value: item, label: item }
      }
      return item
    })
  } else {
    clone.psychAssessmentIds = Array.isArray(clone.psychAssessmentIds) ? clone.psychAssessmentIds : []
  }

  if (clone.type === allAvailableEventTypes.webinar) {
    if (!clone.pricingPlanId && clone.subscriptionPlanId) {
      clone.pricingPlanId = clone.subscriptionPlanId
    }
  }

  if (clone.type === allAvailableEventTypes.therapySession && clone.subtype == null) {
    clone.subtype = EVENT_SUBTYPE_INDIVIDUAL_THERAPY
  }

  const legacyScreening = clone.screeningType
  if (
    clone.type === allAvailableEventTypes.screening &&
    legacyScreening &&
    !clone.targetSpecialtySlug
  ) {
    clone.targetSpecialtySlug = legacyScreening
  }

  if (clone.screeningAllowedIfPossible === undefined || clone.screeningAllowedIfPossible === null) {
    clone.screeningAllowedIfPossible = true
  }

  return clone
}

export default function useEventFormState({
  eventFormModalData,
  oneOnOnePlans,
  mentorClinicAssociationData,
  mentorProfileData,
}) {
  const [selectedPanelMembers, setSelectedPanelMembers] = useState([])
  const [loadingPanelMembers, setLoadingPanelMembers] = useState(true)
  const [isCustomTimeChosen, setIsCustomTimeChosen] = useState(false)
  const [viewPriceCountry, setViewPriceCountry] = useState()
  const [showAddCustomPrice, setShowAddCustomPrice] = useState(false)
  const [oneOnOnePlanToEdit, setOneOnOnePlanToEdit] = useState()
  const [bundlePackToEdit, setBundlePackToEdit] = useState()
  const [showBundlePackEditModal, setShowBundlePackEditModal] = useState(false)

  const eventTemplateData = {
    ...EVENT_TEMPLATE_DATA,
    subscriptionPlanId: oneOnOnePlans?.[0]?.id,
  }

  const [basicEventObject, setBasicEventObject] = useState(
    eventFormModalData ? normalizeInboundEventFormData(eventFormModalData) : eventTemplateData,
  )

  useEffect(() => {
    if (eventFormModalData) {
      setBasicEventObject(normalizeInboundEventFormData(eventFormModalData))
    } else {
      setBasicEventObject({
        ...EVENT_TEMPLATE_DATA,
        subscriptionPlanId: oneOnOnePlans?.[0]?.id,
      })
    }
  }, [eventFormModalData]) // eslint-disable-line react-hooks/exhaustive-deps -- reset only when switching rows

  useEffect(() => {
    if (eventFormModalData || !oneOnOnePlans?.[0]?.id) {
      return
    }
    setBasicEventObject((prev) => {
      if (prev.subscriptionPlanId || prev.pricingPlanId) {
        return prev
      }
      return { ...prev, subscriptionPlanId: oneOnOnePlans[0].id }
    })
  }, [oneOnOnePlans, eventFormModalData])

  useEffect(() => {
    const currentCountryCode = getCountryCode() || 'IN'
    setViewPriceCountry(currentCountryCode.toLowerCase())
  }, [])

  const panelIdsKey = (basicEventObject?.panelIds || []).filter(Boolean).join(',')

  useEffect(() => {
    let cancelled = false

    async function loadPanelMembers() {
      if (!panelIdsKey) {
        setSelectedPanelMembers([])
        setLoadingPanelMembers(false)
        return
      }
      setLoadingPanelMembers(true)
      const response = await getMPDataWithoutParams(
        `/api/secure/teacher/other-teachers/list?ids=${panelIdsKey}`,
      )
      if (cancelled) {
        return
      }
      if (response.status === 200) {
        const ids = panelIdsKey ? panelIdsKey.split(',') : []
        const toSet = ids.map((id) => {
          const foundTeacher = response.data.find((teacher) => teacher.id === id) || {
            id,
            name: 'Unknown',
          }
          return foundTeacher
        })
        setSelectedPanelMembers(toSet)
      }
      setLoadingPanelMembers(false)
    }

    loadPanelMembers()
    return () => {
      cancelled = true
    }
  }, [panelIdsKey])

  function changeLocationBasedOnMentorOrClinicLocation(locationObject, contextId) {
    const contextIdToUse = contextId
    if (contextIdToUse) {
      const foundClinic = mentorClinicAssociationData?.find(
        (clinic) => clinic?.id === contextIdToUse,
      )
      setBasicEventObject((prev) => ({
        ...prev,
        location: locationObject ? locationObject : foundClinic.location,
      }))
    } else {
      setBasicEventObject((prev) => ({
        ...prev,
        location: locationObject ? locationObject : mentorProfileData?.location,
      }))
    }
  }

  useEffect(() => {
    changeLocationBasedOnMentorOrClinicLocation(
      basicEventObject?.location,
      basicEventObject?.contextId,
    )
  }, [mentorClinicAssociationData, mentorProfileData]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const allDurations = getDurationOptions()
    if (
      !allDurations?.some(
        (item) => parseInt(item?.props?.value, 10) === basicEventObject?.durationInMins,
      )
    ) {
      setIsCustomTimeChosen(true)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    basicEventObject,
    setBasicEventObject,
    selectedPanelMembers,
    setSelectedPanelMembers,
    loadingPanelMembers,
    setLoadingPanelMembers,
    isCustomTimeChosen,
    setIsCustomTimeChosen,
    viewPriceCountry,
    setViewPriceCountry,
    showAddCustomPrice,
    setShowAddCustomPrice,
    oneOnOnePlanToEdit,
    setOneOnOnePlanToEdit,
    bundlePackToEdit,
    setBundlePackToEdit,
    showBundlePackEditModal,
    setShowBundlePackEditModal,
    changeLocationBasedOnMentorOrClinicLocation,
  }
}
